import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, type NotificationItem } from "@/hooks/useAuth";
import FullscreenPreview from "@/pages/dashboard/components/FullscreenPreview";
import { downloadFile } from "@/lib/download";
import VideoThumbnail from "@/components/base/VideoThumbnail";
import { resolveUploadUrl } from "@/lib/fileStorage";

interface GeneticMaterialItem {
  id: number;
  client_id: number;
  material_type: string;
  frozen_count: number;
  freeze_date: string | null;
  end_date: string | null;
  current_status: string;
  used_count: number;
  notes: string | null;
}

interface ProgressItem {
  id: number;
  client_id: number;
  stage_id: number;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  notes: string | null;
  media: Array<{ id: number; url: string; type: string; title: string; storage_key?: string; mime_type?: string }>;
  documents: Array<{ id: number; url: string; type: string; title: string; storage_key?: string; mime_type?: string }>;
}

interface Stage {
  id: number;
  name: string;
  description: string;
  sort_order: number;
  icon: string;
  service_plan: string;
  has_fee: boolean;
}

interface Milestone {
  id: number;
  title: string;
  description: string | null;
  date: string;
  type: string;
  media: Array<{ url: string; type: string; title: string }>;
}

interface DashboardData {
  client: {
    id: number;
    name: string;
    phone: string;
    avatar: string | null;
    status: string;
    advisor_name?: string;
    advisor_phone?: string;
    country?: string;
    service_plan?: string;
    partner_name?: string;
    is_single?: boolean;
    created_at?: string;
  };
  currentStage: string | null;
  lastUpdated: string | null;
  stages: Stage[];
  progress: ProgressItem[];
  milestones: Milestone[];
  geneticMaterials: GeneticMaterialItem[];
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateTime(dateStr: string | null) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function getStatusColor(status: string) {
  switch (status) {
    case "completed":
      return "bg-[#c9a96e] border-[#c9a96e] text-white";
    case "in_progress":
      return "bg-[#1a3a5c] border-[#1a3a5c] text-white";
    default:
      return "bg-white border-gray-200 text-gray-400";
  }
}

function getStatusLabel(status: string) {
  switch (status) {
    case "completed": return "已完成";
    case "in_progress": return "进行中";
    default: return "待开始";
  }
}

function getStatusRing(status: string) {
  switch (status) {
    case "completed": return "ring-[#c9a96e]";
    case "in_progress": return "ring-[#1a3a5c]";
    default: return "ring-gray-200";
  }
}

function getTypeStyle(type: string) {
  switch (type) {
    case "success": return "bg-emerald-50 border-emerald-200 text-emerald-700";
    case "warning": return "bg-amber-50 border-amber-200 text-amber-700";
    case "error": return "bg-red-50 border-red-200 text-red-700";
    default: return "bg-[#1a3a5c]/5 border-gray-200 text-[#1a3a5c]";
  }
}

function getTypeIcon(type: string) {
  switch (type) {
    case "success": return "ri-checkbox-circle-line";
    case "warning": return "ri-alert-line";
    case "error": return "ri-close-circle-line";
    default: return "ri-information-line";
  }
}

function getTypeLabel(type: string) {
  switch (type) {
    case "success": return "好消息";
    case "warning": return "提醒";
    case "error": return "紧急";
    default: return "系统";
  }
}

function getServicePlanLabel(plan: string | null | undefined) {
  if (!plan) return "未知方案";
  const map: Record<string, string> = {
    surrogacy_has_embryo: "单助孕 · 已有胚胎",
    surrogacy_transfer_embryo: "单助孕 · 转入胚胎",
    ivf_self: "试管助孕 · 自精自卵",
    ivf_donor: "试管助孕 · 捐赠助孕",
    embryo_transport: "胚胎转运 · 胚胎转出",
    fertility_preservation: "生育力保存 · 生育力保存",
  };
  return map[plan] || plan;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { client, loading: authLoading, logout, fetchDashboard, fetchNotifications, markNotificationRead } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<"stages" | "media" | "notifications">("stages");
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [previewMedia, setPreviewMedia] = useState<{ url: string; type: string; title: string; stageId?: number } | null>(null);
  const [fullscreenPreview, setFullscreenPreview] = useState<{ url: string; type: string; title: string } | null>(null);
  const [fullscreenOrigin, setFullscreenOrigin] = useState<DOMRect | null>(null);
  const thumbnailRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const [geneticMaterials, setGeneticMaterials] = useState<GeneticMaterialItem[]>([]);

  useEffect(() => {
    if (!authLoading && !client) {
      navigate("/");
    }
  }, [authLoading, client, navigate]);

  useEffect(() => {
    if (!client) return;
    const load = async () => {
      setLoading(true);
      const res = await fetchDashboard();
      if (res.success && res.data) {
        setData(res.data);
        setGeneticMaterials(res.data.geneticMaterials || []);
        setLoading(false);
        return;
      }
      // Auth failure: silent redirect, don't flash error
      const errMsg = res.error || "";
      if (errMsg === "未登录" || errMsg.includes("未登录") || errMsg.includes("过期") || errMsg.includes("token")) {
        logout();
        navigate("/", { replace: true });
        return;
      }
      setLoading(false);
      setError(errMsg || "加载失败");
    };
    load();
  }, [client, fetchDashboard, logout, navigate]);

  const loadNotifications = useCallback(async () => {
    const res = await fetchNotifications();
    if (res.success) {
      setNotifications(res.notifications);
      setUnreadCount(res.unreadCount);
    }
  }, [fetchNotifications]);

  useEffect(() => {
    if (client) {
      loadNotifications();
    }
  }, [client, loadNotifications]);

  const handleMarkRead = async (id: number) => {
    const res = await markNotificationRead(id);
    if (res.success) {
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const toggleStage = (stageId: number) => {
    setExpandedStages((prev) => {
      const next = new Set(prev);
      if (next.has(stageId)) { next.delete(stageId); } else { next.add(stageId); }
      return next;
    });
  };

  const handleDownload = (url: string, title: string) => {
    downloadFile(url, title || "download");
  };

  const servicePlan = data?.client?.service_plan || client?.service_plan;
  // dashboard 返回的 progress 已经是该客户方案对应的阶段进度（服务端已过滤），直接使用
  const filteredProgress = data?.progress || [];

  // 从 progress 数据中重建 stages 列表（含阶段名），按 stage_id 排序
  const filteredStages = filteredProgress
    .slice()
    .sort((a, b) => (a.stage_id || 0) - (b.stage_id || 0))
    .map((p) => ({
      id: p.stage_id,
      name: (p as any).stage_name || `阶段 ${p.stage_id}`,
      description: (p as any).stage_description || "",
      sort_order: p.stage_id,
      icon: (p as any).icon || "",
      service_plan: servicePlan || "",
      has_fee: false,
    }));

  const progressMap = new Map<number, ProgressItem>();
  filteredProgress.forEach((p) => progressMap.set(p.stage_id, p));

  const completedCount = filteredProgress.filter((p) => p.status === "completed").length;
  const inProgressCount = filteredProgress.filter((p) => p.status === "in_progress").length;
  const totalStages = filteredStages.length;
  const overallPercent = totalStages > 0 ? Math.round((completedCount / totalStages) * 100) : 0;

  const allMedia = filteredProgress.flatMap((p) =>
    p.media.map((m) => ({ ...m, stageName: filteredStages.find((s) => s.id === p.stage_id)?.name || "" }))
  ).sort((a, b) => {
    // Video first
    if (a.type === "video" && b.type !== "video") return -1;
    if (a.type !== "video" && b.type === "video") return 1;
    return 0;
  });

  const allDocs = filteredProgress.flatMap((p) =>
    p.documents.map((d) => ({ ...d, stageName: filteredStages.find((s) => s.id === p.stage_id)?.name || "" }))
  );

  const sortedNotifications = [...notifications].sort((a, b) => {
    if (a.is_read !== b.is_read) return a.is_read ? 1 : -1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const clientName = data?.client?.name || client?.name || "";
  const partnerName = data?.client?.partner_name || client?.partner_name;
  const isSingle = data?.client?.is_single ?? client?.is_single ?? true;
  const displayName = !isSingle && partnerName ? `${clientName} & ${partnerName}` : clientName;
  const country = data?.client?.country || client?.country;
  const currentStage = data?.currentStage || null;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#f8f5f0] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-[#c9a96e] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-500">加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#f8f5f0] flex items-center justify-center px-5">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-gray-300 mb-3"></i>
          <p className="text-sm text-gray-500 mb-4">{error || "数据加载失败"}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-[#c9a96e] text-white rounded-full text-sm cursor-pointer whitespace-nowrap">重试</button>
        </div>
      </div>
    );
  }

  const { lastUpdated, client: clientData } = data;

  return (
    <div className="min-h-screen bg-[#f8f5f0] pb-24">
      {/* Header */}
      <header className="bg-[#1a3a5c] text-white">
        <div className="max-w-lg mx-auto px-5 pt-8 pb-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/10 overflow-hidden flex items-center justify-center p-1">
                <img
                  src="/logo.png"
                  alt="RTB"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-base font-bold">RTB 客户中心</h1>
                <p className="text-[11px] text-white/60">试管助孕全流程追踪</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleLogout}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
              >
                <i className="ri-logout-box-r-line text-sm"></i>
              </button>
            </div>
          </div>

          {/* Client Info Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#c9a96e]/20 flex items-center justify-center shrink-0">
                <i className="ri-user-smile-line text-[#c9a96e]"></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{displayName}</p>
                <p className="text-[11px] text-white/50">{clientData.client_code || clientData.phone}</p>
              </div>
              <div className="ml-auto text-right shrink-0">
                <p className="text-lg font-bold text-[#c9a96e]">{overallPercent}%</p>
                <p className="text-[10px] text-white/50">总体进度</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              {country && (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70">
                  <i className="ri-map-pin-line"></i>{country}
                </span>
              )}
              {servicePlan && (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70">
                  <i className="ri-hospital-line"></i>{getServicePlanLabel(servicePlan)}
                </span>
              )}
              {currentStage && (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#c9a96e]/20 text-[#c9a96e]">
                  <i className="ri-loader-2-line animate-spin"></i>{currentStage}
                </span>
              )}
            </div>
            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#c9a96e] rounded-full transition-all duration-1000" style={{ width: `${overallPercent}%` }}></div>
            </div>
            <div className="flex items-center justify-between mt-2 text-[10px] text-white/50">
              <span>已完成 {completedCount} 个阶段</span>
              <span>共 {totalStages} 个阶段</span>
            </div>
            {geneticMaterials.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-[10px] text-white/40 mb-2 flex items-center gap-1"><i className="ri-dna-line"></i>遗传物质</p>
                <div className="space-y-1.5">
                  {geneticMaterials.map((gm) => {
                    const labels: Record<string, string> = { sperm: "精子", egg: "卵子", embryo: "胚胎" };
                    const units: Record<string, string> = { sperm: "管", egg: "枚", embryo: "枚" };
                    const remaining = (gm.frozen_count || 0) - (gm.used_count || 0);
                    const endDate = gm.end_date ? new Date(gm.end_date) : null;
                    const isAlert = endDate && gm.current_status !== "销毁" && gm.current_status !== "转运" && (endDate.getTime() - Date.now()) < 30 * 24 * 60 * 60 * 1000;
                    const hasDates = gm.freeze_date || gm.end_date;
                    return (
                      <div key={gm.material_type} className={`rounded-lg px-2 py-1.5 ${isAlert ? "bg-red-500/15" : "bg-white/5"}`}>
                        <div className="flex items-center gap-1.5 text-[10px]">
                          <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                            <i className={`ri-${gm.material_type === "sperm" ? "men" : gm.material_type === "egg" ? "women" : "heart-pulse"}-line text-[10px] ${isAlert ? "text-red-300" : "text-[#c9a96e]"}`}></i>
                          </span>
                          <span className={`font-medium ${isAlert ? "text-red-300" : "text-white/70"}`}>{labels[gm.material_type]}</span>
                          <span className={isAlert ? "text-red-300/80" : "text-white/40"}>{gm.frozen_count || 0}{units[gm.material_type]} · 余{remaining}{units[gm.material_type]}</span>
                          {hasDates && (
                            <span className={`mx-1.5 ${isAlert ? "text-red-300/80" : "text-white/50"}`}>
                              {gm.freeze_date ? formatDate(gm.freeze_date) : "—"} - {gm.end_date ? formatDate(gm.end_date) : "—"}
                            </span>
                          )}
                          {isAlert && <span className="text-[9px] text-red-300 shrink-0"><i className="ri-alert-fill"></i></span>}
                          <span className={`ml-auto px-1.5 py-0.5 rounded text-[9px] font-medium shrink-0 ${gm.current_status === "在存" ? "bg-emerald-500/20 text-emerald-300" : gm.current_status === "销毁" ? "bg-white/10 text-white/40" : "bg-amber-500/20 text-amber-300"}`}>{gm.current_status}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Last Updated */}
      {lastUpdated && (
        <div className="max-w-lg mx-auto px-5 -mt-3">
          <div className="bg-white rounded-xl px-4 py-2.5 flex items-center gap-2">
            <i className="ri-time-line text-[#c9a96e] text-xs"></i>
            <p className="text-[11px] text-gray-500">最后更新：<span className="text-[#1a3a5c] font-medium">{formatDateTime(lastUpdated)}</span></p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="max-w-lg mx-auto px-5 mt-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-[#c9a96e]">{completedCount}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">已完成</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-[#1a3a5c]">{inProgressCount}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">进行中</p>
          </div>
          <div className="bg-white rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-gray-400">{totalStages - completedCount - inProgressCount}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">待开始</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-lg mx-auto px-5 mt-5">
        <div className="flex bg-white rounded-xl p-1 overflow-x-auto">
          {(["stages", "media", "notifications"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setPreviewMedia(null); }}
              className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap relative ${activeTab === tab ? "bg-[#1a3a5c] text-white" : "text-gray-500 hover:text-[#1a3a5c]"}`}
            >
              {tab === "stages" && "阶段进度"}
              {tab === "media" && "影像资料"}
              {tab === "notifications" && (
                <span className="flex items-center justify-center gap-1">
                  消息通知
                  {unreadCount > 0 && activeTab !== "notifications" && (
                    <span className="w-4 h-4 flex items-center justify-center bg-red-500 text-white text-[9px] rounded-full shrink-0">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-5 mt-4 space-y-4">
        {/* Stages Tab */}
        {activeTab === "stages" && (
          <div className="space-y-3">
            {filteredStages.map((stage, index) => {
              const p = progressMap.get(stage.id);
              const isExpanded = expandedStages.has(stage.id);
              const isCompleted = p?.status === "completed";
              const isInProgress = p?.status === "in_progress";

              return (
                <div key={stage.id} className={`bg-white rounded-xl overflow-hidden transition-all ${isExpanded ? "ring-1 ring-[#c9a96e]/30" : ""}`}>
                  <button
                    onClick={() => toggleStage(stage.id)}
                    className="w-full flex items-center gap-3 p-4 text-left cursor-pointer"
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 transition-all ring-2 ring-offset-1 ${getStatusRing(p?.status || "pending")} ${isCompleted ? "bg-[#c9a96e] text-white" : isInProgress ? "bg-[#1a3a5c] text-white" : "bg-gray-50 text-gray-400"}`}>
                      {isCompleted ? <i className="ri-check-line text-sm"></i> : <span className="text-xs font-semibold">{index + 1}</span>}
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <p className={`text-sm font-semibold truncate ${isCompleted || isInProgress ? "text-[#1a3a5c]" : "text-gray-400"}`}>{stage.name}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${getStatusColor(p?.status || "pending")}`}>{getStatusLabel(p?.status || "pending")}</span>
                    </div>
                    <i className={`ri-arrow-down-s-line text-gray-400 transition-transform shrink-0 ${isExpanded ? "rotate-180" : ""}`}></i>
                  </button>

                  {isExpanded && p && (
                    <div className="px-4 pb-4 border-t border-gray-50 pt-3">
                      <div className="space-y-3">
                        {(p.started_at || p.completed_at || (index === 0 && data?.client?.created_at)) && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <i className="ri-calendar-line text-[#c9a96e]"></i>
                            <span>
                              {(() => {
                                const start = p.started_at || (index === 0 ? data?.client?.created_at : null);
                                const end = p.completed_at;
                                if (end && start) return `${formatDate(start)} - ${formatDate(end)}`;
                                if (end && !start) return `完成于 ${formatDate(end)}`;
                                if (start && !end) return `开始于 ${formatDate(start)}`;
                                return "进行中";
                              })()}
                            </span>
                          </div>
                        )}

                        {p.media && p.media.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                              <i className="ri-image-line text-[#c9a96e]"></i>影像记录
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              {p.media.map((m, i) => {
                                const thumbKey = `stage-${stage.id}-${i}`;
                                return (
                                  <div
                                    key={i}
                                    ref={(el) => { if (el) thumbnailRefs.current.set(thumbKey, el); else thumbnailRefs.current.delete(thumbKey); }}
                                    className={`rounded-lg overflow-hidden bg-gray-100 aspect-[4/3] relative group cursor-pointer ${previewMedia?.url === m.url ? "ring-2 ring-[#c9a96e]" : ""}`}
                                    onClick={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setFullscreenOrigin(rect);
                                      const resolvedUrl = resolveUploadUrl(m.url);
                                      setPreviewMedia({ url: resolvedUrl, type: m.type, title: m.title, stageId: stage.id });
                                      setFullscreenPreview({ url: resolvedUrl, type: m.type, title: m.title });
                                    }}
                                  >
                                    {m.type === "video" ? (
                                      <VideoThumbnail src={resolveUploadUrl(m.url)} title={m.title} className="w-full h-full" />
                                    ) : (
                                      <img
                                        src={resolveUploadUrl(m.url)}
                                        alt={m.title}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='%239ca3af' font-size='12'%3E加载失败%3C/text%3E%3C/svg%3E";
                                        }}
                                      />
                                    )}
                                    <div className="absolute inset-0 bg-black/20 md:bg-black/0 md:group-hover:bg-black/30 transition-colors flex items-center justify-center md:opacity-0 md:group-hover:opacity-100">
                                      <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 flex items-center justify-center text-[#1a3a5c]">
                                        <i className={`${m.type === "video" ? "ri-play-fill" : "ri-eye-line"} text-base md:text-lg`}></i>
                                      </div>
                                    </div>
                                    <p className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-black/50 text-[10px] text-white truncate">{m.title}</p>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {p.documents && p.documents.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                              <i className="ri-file-text-line text-[#c9a96e]"></i>相关文档
                            </p>
                            <div className="space-y-1.5">
                              {p.documents.map((d, i) => (
                                <div key={i} className="flex items-center gap-2 bg-[#f8f5f0] rounded-lg px-3 py-2 hover:bg-[#c9a96e]/5 transition-colors cursor-pointer" onClick={() => handleDownload(d.url, d.title)}>
                                  <i className={`${d.type === "pdf" ? "ri-file-pdf-line" : "ri-file-text-line"} text-[#c9a96e] text-sm`}></i>
                                  <span className="text-xs text-[#1a3a5c] truncate flex-1">{d.title}</span>
                                  <i className="ri-download-line text-gray-400 text-xs"></i>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Media Tab */}
        {activeTab === "media" && (
          <div className="space-y-4">
            {allMedia.length === 0 && allDocs.length === 0 && (
              <div className="bg-white rounded-xl p-8 text-center">
                <i className="ri-image-line text-3xl text-gray-300 mb-2"></i>
                <p className="text-sm text-gray-400">暂无影像资料</p>
              </div>
            )}

            {allMedia.length > 0 && (
              <div className="bg-white rounded-xl p-4">
                <h3 className="text-sm font-semibold text-[#1a3a5c] mb-3 flex items-center gap-1.5">
                  <i className="ri-image-line text-[#c9a96e]"></i>影像记录
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {allMedia.map((m, i) => {
                    const thumbKey = `media-${i}`;
                    return (
                      <div
                        key={i}
                        ref={(el) => { if (el) thumbnailRefs.current.set(thumbKey, el); else thumbnailRefs.current.delete(thumbKey); }}
                        className={`rounded-xl overflow-hidden bg-gray-100 aspect-[4/3] relative group cursor-pointer ${previewMedia?.url === m.url ? "ring-2 ring-[#c9a96e]" : ""}`}
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setFullscreenOrigin(rect);
                          const resolvedUrl = resolveUploadUrl(m.url);
                          setPreviewMedia({ url: resolvedUrl, type: m.type, title: m.title });
                          setFullscreenPreview({ url: resolvedUrl, type: m.type, title: m.title });
                        }}
                      >
                        {m.type === "video" ? (
                          <VideoThumbnail src={resolveUploadUrl(m.url)} title={m.title} className="w-full h-full" />
                        ) : (
                          <img
                            src={resolveUploadUrl(m.url)}
                            alt={m.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='%239ca3af' font-size='12'%3E加载失败%3C/text%3E%3C/svg%3E";
                            }}
                          />
                        )}
                        <div className="absolute inset-0 bg-black/20 md:bg-black/0 md:group-hover:bg-black/30 transition-colors flex items-center justify-center md:opacity-0 md:group-hover:opacity-100">
                          <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-white/90 flex items-center justify-center text-[#1a3a5c]">
                            <i className={`${m.type === "video" ? "ri-play-fill" : "ri-eye-line"} text-base md:text-lg`}></i>
                          </div>
                        </div>
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-transparent to-transparent p-3">
                          <p className="text-xs text-white font-medium">{m.title}</p>
                          <p className="text-[10px] text-white/70">{m.stageName}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {allDocs.length > 0 && (
              <div className="bg-white rounded-xl p-4">
                <h3 className="text-sm font-semibold text-[#1a3a5c] mb-3 flex items-center gap-1.5">
                  <i className="ri-folder-line text-[#c9a96e]"></i>文档资料
                </h3>
                <div className="space-y-2">
                  {allDocs.map((d, i) => (
                    <div key={i} className="flex items-center gap-3 bg-[#f8f5f0] rounded-xl px-4 py-3 hover:bg-[#c9a96e]/5 transition-colors cursor-pointer" onClick={() => handleDownload(d.url, d.title)}>
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#1a3a5c]/8 shrink-0">
                        <i className={`${d.type === "pdf" ? "ri-file-pdf-line" : "ri-file-text-line"} text-[#c9a96e] text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1a3a5c] truncate">{d.title}</p>
                        <p className="text-[11px] text-gray-400">{d.stageName}</p>
                      </div>
                      <i className="ri-download-line text-gray-400 text-sm shrink-0"></i>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div className="space-y-3">
            {sortedNotifications.length === 0 && (
              <div className="bg-white rounded-xl p-8 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-notification-off-line text-2xl text-gray-300"></i>
                </div>
                <p className="text-sm text-gray-400">暂无消息通知</p>
                <p className="text-[11px] text-gray-300 mt-1">关键节点更新将第一时间推送</p>
              </div>
            )}
            {sortedNotifications.map((n) => (
              <div key={n.id} className={`bg-white rounded-xl p-4 border-l-[3px] transition-all ${n.is_read ? "border-transparent opacity-80" : getTypeStyle(n.type)}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full shrink-0 ${n.is_read ? "bg-gray-100 text-gray-300" : "bg-white/60"}`}>
                    <i className={`${getTypeIcon(n.type)} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className={`text-sm font-semibold truncate ${n.is_read ? "text-gray-500" : "text-[#1a3a5c]"}`}>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium mr-1.5 ${n.type === "error" ? "bg-red-100 text-red-600" : n.type === "warning" ? "bg-amber-100 text-amber-600" : n.type === "success" ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>{getTypeLabel(n.type)}</span>
                        {n.title}
                      </p>
                      <span className="text-[10px] text-gray-400 shrink-0">{formatDateTime(n.scheduled_at || n.created_at)}</span>
                    </div>
                    <p className={`text-xs leading-relaxed ${n.is_read ? "text-gray-400" : "text-gray-600"}`}>{n.content}</p>
                    {!n.is_read && (
                      <button onClick={() => handleMarkRead(n.id)} className="mt-2 text-[11px] text-[#c9a96e] font-medium hover:underline cursor-pointer whitespace-nowrap">标记为已读</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="h-8"></div>

      {clientData.advisor_name && clientData.advisor_phone && (
        <div className="max-w-lg mx-auto px-5 pb-6">
          <div className="bg-white rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1.5">
              <i className="ri-customer-service-2-line text-[#c9a96e]"></i>
              <p className="text-xs text-gray-500">如有疑问，请联系您的专属顾问</p>
            </div>
            <p className="text-sm font-semibold text-[#1a3a5c]">
              {clientData.advisor_name}
              <a href={`tel:${clientData.advisor_phone.replace(/\s/g, "")}`} className="ml-2 text-[#c9a96e] hover:underline cursor-pointer">
                <i className="ri-phone-line mr-0.5"></i>{clientData.advisor_phone}
              </a>
            </p>
          </div>
        </div>
      )}

      <footer className="bg-[#1a3a5c] text-center py-4">
        <p className="text-[11px] text-white/40">RTB 试管助孕中心</p>
        <p className="text-[10px] text-white/30 mt-0.5">客户全周期管理 CRM 系统</p>
        <p className="text-[10px] text-white/25 mt-1">&copy; 2026 RTB Fertility. All Rights Reserved.</p>
      </footer>

      {/* Fullscreen Preview */}
      {fullscreenPreview && (
        <FullscreenPreview
          media={fullscreenPreview}
          originRect={fullscreenOrigin}
          onClose={() => { setFullscreenPreview(null); setFullscreenOrigin(null); }}
          onDownload={() => handleDownload(fullscreenPreview.url, fullscreenPreview.title)}
        />
      )}
    </div>
  );
}